"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useStrategyMatrixQueryParamsSync = void 0;
const react_1 = require("react");
const ramda_1 = require("ramda");
const react_redux_1 = require("react-redux");
const react_router_1 = require("react-router");
const strategy_matrix_query_params_hydrators_map_1 = require("./strategy-matrix-query-params-hydrators.map");
const strategy_matrix_query_params_sync_helpers_1 = require("./strategy-matrix-query-params-sync.helpers");
const strategy_matrix_query_params_constants_1 = require("./strategy-matrix-query-params.constants");
const strategy_matrix_query_params_mappers_1 = require("./strategy-matrix-query-params.mappers");
const strategy_matrix_query_params_models_1 = require("./strategy-matrix-query-params.models");
const strategy_matrix_query_params_utils_1 = require("./strategy-matrix-query-params.utils");
const hooks_1 = require("../../client/hooks");
const store_1 = require("../../store");
const view_query_params_1 = require("../../../common/view-query-params");
const hooks_2 = require("../../../../hooks");
const localization_1 = require("../../../../localization");
const store_2 = require("../../../../store");
const useStrategyMatrixQueryParamsSync = () => {
    const dispatch = (0, react_redux_1.useDispatch)();
    const { addError } = (0, hooks_2.useNotification)();
    const { formatMessage: t } = (0, localization_1.useLocalization)();
    const { hash, pathname, search } = (0, react_router_1.useLocation)();
    const navigate = (0, react_router_1.useNavigate)();
    const selectedCatalogId = (0, store_2.useSelectedCatalogId)();
    const selectedProjectId = (0, store_2.useSelectedProjectId)();
    const productTreeRootNodeId = (0, store_2.useProductTreeRootNodeId)();
    const hydratedStateRef = (0, react_1.useRef)();
    const pendingHydrationRef = (0, react_1.useRef)();
    const ignoredHydrationErrorKeyRef = (0, react_1.useRef)();
    const writtenQueryKeyRef = (0, react_1.useRef)();
    const { extendedFilter = [], selectedAttributes = [], selectedDisplayTable = [], } = (0, store_1.useStrategyMatrixData)();
    const { data: allL1Categories = [], error: allL1CategoriesError, isLoading: areAllL1CategoriesLoading, } = (0, hooks_1.useGetAllL1CategoriesAndTerminalNodesForStrategyMatrix)({
        catalogId: selectedCatalogId,
        options: {
            fetchPolicy: 'cache-and-network',
            skip: !productTreeRootNodeId || !selectedCatalogId,
        },
        productTreeNodeId: productTreeRootNodeId,
    });
    const allCategoryIds = (0, react_1.useMemo)(() => allL1Categories.map(({ id }) => id), [allL1Categories]);
    const allChildIdsByCategoryId = (0, react_1.useMemo)(() => (allL1Categories.reduce((acc, category) => {
        var _a, _b, _c;
        const children = (_c = (_b = (_a = category.descendants) === null || _a === void 0 ? void 0 : _a.edges) === null || _b === void 0 ? void 0 : _b.map((edge) => { var _a, _b; return (_b = (_a = edge === null || edge === void 0 ? void 0 : edge.node) === null || _a === void 0 ? void 0 : _a.descendant) === null || _b === void 0 ? void 0 : _b.id; }).filter((id) => Boolean(id))) !== null && _c !== void 0 ? _c : [];
        acc[category.id] = children;
        return acc;
    }, {})), [allL1Categories]);
    const encodedQueryState = (0, react_1.useMemo)(() => new URLSearchParams(search).get(view_query_params_1.VIEW_QUERY_STATE_QUERY_KEY), [search]);
    const stateFromUrl = (0, react_1.useMemo)(() => (0, strategy_matrix_query_params_utils_1.decodeStrategyMatrixQueryState)(encodedQueryState), [encodedQueryState]);
    const [terminalNodeIdFromUrl, setTerminalNodeIdFromUrl] = (0, react_1.useState)(stateFromUrl === null || stateFromUrl === void 0 ? void 0 : stateFromUrl[strategy_matrix_query_params_models_1.StrategyMatrixViewQueryStateField.TerminalNodeId]);
    const viewState = (0, react_1.useMemo)(() => (0, strategy_matrix_query_params_mappers_1.getStrategyMatrixQueryState)({
        allChildIdsByCategoryId,
        allCategoryIds,
        catalogId: selectedCatalogId,
        extendedFilter,
        projectId: selectedProjectId,
        selectedAttributes,
        selectedDisplayTable,
    }), [
        allChildIdsByCategoryId,
        allCategoryIds,
        extendedFilter,
        selectedAttributes,
        selectedCatalogId,
        selectedDisplayTable,
        selectedProjectId,
    ]);
    const encodedViewState = (0, react_1.useMemo)(() => ((0, strategy_matrix_query_params_utils_1.encodeStrategyMatrixQueryState)(viewState, strategy_matrix_query_params_constants_1.strategyMatrixQueryStateDefaults)), [viewState]);
    const hydrationKey = selectedCatalogId ? encodedQueryState !== null && encodedQueryState !== void 0 ? encodedQueryState : '' : 'disabled';
    const [hydratedQueryKey, setHydratedQueryKey] = (0, react_1.useState)(selectedCatalogId ? null : hydrationKey);
    const isHydrated = !selectedCatalogId || hydratedQueryKey === hydrationKey || writtenQueryKeyRef.current === hydrationKey;
    const handleHydrate = (0, react_1.useCallback)((state) => {
        if (!selectedCatalogId)
            return;
        (0, strategy_matrix_query_params_hydrators_map_1.hydrateStrategyMatrixQueryState)({
            allCategoryIds,
            allChildIdsByCategoryId,
            catalogId: selectedCatalogId,
            dispatch,
            state,
        });
        setTerminalNodeIdFromUrl(state[strategy_matrix_query_params_models_1.StrategyMatrixViewQueryStateField.TerminalNodeId]);
    }, [allCategoryIds, allChildIdsByCategoryId, dispatch, selectedCatalogId]);
    (0, react_1.useEffect)(() => {
        if (!selectedCatalogId) {
            setHydratedQueryKey(hydrationKey);
            return;
        }
        if (writtenQueryKeyRef.current === hydrationKey) {
            setHydratedQueryKey(hydrationKey);
            return;
        }
        const decodedQueryState = (0, strategy_matrix_query_params_utils_1.decodeStrategyMatrixQueryState)(encodedQueryState);
        if (!decodedQueryState || (0, ramda_1.equals)(decodedQueryState, hydratedStateRef.current)) {
            setHydratedQueryKey(hydrationKey);
            return;
        }
        const extendedFilterFromUrl = decodedQueryState[strategy_matrix_query_params_models_1.StrategyMatrixViewQueryStateField.ExtendedFilter];
        if (allL1CategoriesError && (0, strategy_matrix_query_params_sync_helpers_1.isCompactExtendedFilterState)(extendedFilterFromUrl)) {
            if (ignoredHydrationErrorKeyRef.current !== hydrationKey) {
                ignoredHydrationErrorKeyRef.current = hydrationKey;
                addError(t({ key: 'GENERAL_ERROR_MESSAGE' }), allL1CategoriesError);
            }
            hydratedStateRef.current = decodedQueryState;
            pendingHydrationRef.current = undefined;
            setHydratedQueryKey(hydrationKey);
            return;
        }
        const shouldDeferHydration = (0, strategy_matrix_query_params_sync_helpers_1.shouldDeferCompactHydration)({
            allCategoryIds,
            allChildIdsByCategoryId,
            areAllL1CategoriesLoading,
            extendedFilter: extendedFilterFromUrl,
        });
        if (shouldDeferHydration) {
            if (allL1CategoriesError) {
                if (ignoredHydrationErrorKeyRef.current !== hydrationKey) {
                    ignoredHydrationErrorKeyRef.current = hydrationKey;
                    addError(t({ key: 'GENERAL_ERROR_MESSAGE' }), allL1CategoriesError);
                }
                hydratedStateRef.current = decodedQueryState;
                pendingHydrationRef.current = undefined;
                setHydratedQueryKey(hydrationKey);
            }
            return;
        }
        hydratedStateRef.current = decodedQueryState;
        pendingHydrationRef.current = decodedQueryState;
        handleHydrate(decodedQueryState);
        setHydratedQueryKey(hydrationKey);
    }, [
        allCategoryIds,
        allL1CategoriesError,
        addError,
        areAllL1CategoriesLoading,
        allChildIdsByCategoryId,
        encodedQueryState,
        handleHydrate,
        hydrationKey,
        selectedCatalogId,
        t,
    ]);
    (0, react_1.useEffect)(() => {
        if (!selectedCatalogId || !isHydrated)
            return;
        const decodedQueryState = (0, strategy_matrix_query_params_utils_1.decodeStrategyMatrixQueryState)(encodedQueryState);
        const normalizedEncodedQueryState = decodedQueryState
            ? (0, strategy_matrix_query_params_utils_1.encodeStrategyMatrixQueryState)(decodedQueryState, strategy_matrix_query_params_constants_1.strategyMatrixQueryStateDefaults)
            : undefined;
        if (pendingHydrationRef.current
            && (0, ramda_1.equals)(decodedQueryState, pendingHydrationRef.current)
            && normalizedEncodedQueryState !== encodedViewState) {
            pendingHydrationRef.current = undefined;
            return;
        }
        pendingHydrationRef.current = undefined;
        if (normalizedEncodedQueryState === encodedViewState)
            return;
        const nextSearch = (0, strategy_matrix_query_params_sync_helpers_1.getSearchWithViewState)(search, encodedViewState);
        if (nextSearch === search)
            return;
        writtenQueryKeyRef.current = encodedViewState !== null && encodedViewState !== void 0 ? encodedViewState : '';
        navigate({ hash, pathname, search: nextSearch }, { replace: true });
    }, [encodedQueryState, encodedViewState, hash, isHydrated, navigate, pathname, search, selectedCatalogId]);
    return {
        isHydrated,
        setTerminalNodeIdFromUrl,
        terminalNodeIdFromUrl,
    };
};
exports.useStrategyMatrixQueryParamsSync = useStrategyMatrixQueryParamsSync;
